import Swal from "sweetalert2";
import * as Cesium from 'cesium';
import { makeStyles } from "@mui/styles";
import StacInterface from "utils/StacInterface";
import FormControl from '@mui/material/FormControl';
import { useDispatch, useSelector } from "react-redux";
import { MenuItem, Select, Menu } from '@mui/material';
import useViewerStore from '../../store-zustand/viewer';
import withReactContent from "sweetalert2-react-content";
import React, { useState, useRef, useEffect } from "react";
import { getLayerCollectionDetail } from "@/utils/helpers";
import TooltipIconButton from "components/TooltipIconButton";
import { Close, Wallpaper, MoreVert } from '@mui/icons-material';
import { Content, FormItem, Footer, Wrapper } from '../ActionModal';
import { getGlobePositionWithTerrain, generateNodeImage, ToolTipDisplay } from '../MapView/MapUtils';

const useStyles = makeStyles((theme) => ({
  formControl: {
    margin: 0,
    minWidth: 131,
    position: 'relative'
  }
}));

const maxSizes = [
  { value: 1000, label: '1000' },
  { value: 2000, label: '2000' },
  { value: 3000, label: '3000' },
  { value: 4000, label: '4000' },
  { value: 5000, label: '5000' }
];

const formats = [
  { value: 'jpg', label: 'JPG' },
  { value: 'png', label: 'PNG' },
  { value: 'webp', label: 'WEBP' },
  { value: 'tif', label: 'TIFF' }
];

const DataPrintModal = () => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const mouseHandler = useRef();
  const rectanglePositions = useRef([]);
  const rectangleAdded = useRef(false);
  const [exportBtnActive, setExportBtnActive] = useState(false);
  const { mapboxvisibility, setmapboxvisibility } = useViewerStore();
  const selectedMission = useSelector(state => state.asset.selectedMission.split(':')[1]);
  const selectedCollection = useSelector(state => state.asset.selectedMission ? getLayerCollectionDetail(state.asset.selectedMission)[1] : undefined);
  const outputFormatRef = useRef('jpg');
  const maxSizeRef = useRef(4000);
  const boundaryRef = useRef();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const menuOpen = Boolean(anchorEl);
  
  const handleClickMore = (event) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleCloseMenu = () => {
    setAnchorEl(null);
  };
  
  const [formData, setFormData] = useState({
    catalog: selectedCollection.name,
    maxSize: maxSizeRef.current,
    format: outputFormatRef.current
  });

  const catalogRef = useRef(selectedCollection);

  const handleChange = (event, name) => {
    setFormData({ ...formData, [name]: event.target.value });
    if (name === "catalog") {
      catalogRef.current = window.exportable_collections.find(item => item.name === event.target.value);
    }
    if (name === "maxSize") {
      maxSizeRef.current = event.target.value;
    }
    if (name === "format") {
      outputFormatRef.current = event.target.value;
    }

    if (window.exportRectangle) {
      var coords = window.exportRectangle.rectangle.coordinates._value;
      var west = coords.west * Cesium.Math.DEGREES_PER_RADIAN;
      var south = coords.south * Cesium.Math.DEGREES_PER_RADIAN;
      var east = coords.east * Cesium.Math.DEGREES_PER_RADIAN;
      var north = coords.north * Cesium.Math.DEGREES_PER_RADIAN;      
      boundaryRef.current = [[west, north], [east, north], [east, south], [west, south], [west, north]];
      setExportBtnActive(true);
    }
  };

  const removeExportRectangle = () => {
    if (mouseHandler.current) {
      mouseHandler.current.removeInputAction(Cesium.ScreenSpaceEventType.MOUSE_MOVE);
      mouseHandler.current.removeInputAction(Cesium.ScreenSpaceEventType.LEFT_CLICK);
    }

    window.MAIN_CESIUM_VIEWER.entities.remove(window.exportRectangle);
    delete (window.exportRectangle);
    rectanglePositions.current = [];
    rectangleAdded.current = false;

    document.getElementById("cesium-wrapper").style.cursor = 'auto';
  }

  const removeExportPolygon = () => {
    if (mouseHandler.current) {
      mouseHandler.current.removeInputAction(Cesium.ScreenSpaceEventType.MOUSE_MOVE);
      mouseHandler.current.removeInputAction(Cesium.ScreenSpaceEventType.LEFT_CLICK);
    }

    window.MAIN_CESIUM_VIEWER.entities.remove(window.exportPolygon);
    delete (window.exportPolygon);
    activeShapePoints.current = [];
    circles.current.map(c => {
      window.MAIN_CESIUM_VIEWER.entities.remove(c);
    })
    circles.current = [];
    window.MAIN_CESIUM_VIEWER.entities.remove(floatingPoint.current);
    floatingPoint.current = null;
    document.getElementById("cesium-wrapper").style.cursor = 'auto';
  }

  const closeModal = () => {
    removeExportRectangle();
    removeExportPolygon();
    window.MAIN_CESIUM_VIEWER.entities.remove(window.kmlExportPolygon);
    delete (window.kmlExportPolygon)
    window.MAIN_CESIUM_VIEWER.entities.remove(window.geojsonExportPolygon);
    delete (window.geojsonExportPolygon)

    dispatch({ type: 'TOGGLE_PRINT_MODAL', show: false });
    setmapboxvisibility(0);
  }

  const printImage = () => {
    var collectionID = window.exportable_collections.find(item => item.name === formData.catalog).name;
    var data = {
      boundary_coordinates: boundaryRef.current,
      mission_name: selectedMission,
      collection_name: collectionID,
      max_size: maxSizeRef.current,
      output_format: outputFormatRef.current,
    }

    StacInterface.instance.print(data).then(response => {
      if (response?.data?.message) {
        withReactContent(Swal).fire({
          title: "Success",
          html: response.data.message,
        });
      } else {
        var url = response?.data;

        if (url) {
          const link = document.createElement('a');

          link.href = url;
          link.target = "_blank";

          link.click();
          link.remove();
        }
      }
    }).catch((err) => {
      withReactContent(Swal).fire({
        title: "Error",
        html: err,
      });
    })
  }

  const drawRectangle = () => {
    removeExportRectangle();
    removeExportPolygon();
    window.MAIN_CESIUM_VIEWER.entities.remove(window.kmlExportPolygon);
    delete (window.kmlExportPolygon)
    window.MAIN_CESIUM_VIEWER.entities.remove(window.geojsonExportPolygon);
    delete (window.geojsonExportPolygon)

    document.getElementById("cesium-wrapper").style.cursor = 'crosshair';

    mouseHandler.current = new Cesium.ScreenSpaceEventHandler(window.MAIN_CESIUM_VIEWER.scene.canvas);

    mouseHandler.current.setInputAction(function (movement) {
      let newPos = getGlobePositionWithTerrain(window.MAIN_CESIUM_VIEWER, movement.endPosition);
      if (rectanglePositions.current[0] && !rectangleAdded.current)
        rectanglePositions.current[1] = newPos;
      if (rectanglePositions.current.length === 2 && !rectangleAdded.current) {
        if (window.exportRectangle) return
        window.exportRectangle = window.MAIN_CESIUM_VIEWER.entities.add({
          rectangle: {
            coordinates: new Cesium.CallbackProperty(() => {
              if (rectanglePositions.current[0].longitude < rectanglePositions.current[1].longitude) {
                var west = rectanglePositions.current[0].longitude;
                var east = rectanglePositions.current[1].longitude;
              } else {
                var west = rectanglePositions.current[1].longitude;
                var east = rectanglePositions.current[0].longitude;
              }

              if (rectanglePositions.current[0].latitude < rectanglePositions.current[1].latitude) {
                var south = rectanglePositions.current[0].latitude;
                var north = rectanglePositions.current[1].latitude;
              } else {
                var south = rectanglePositions.current[1].latitude;
                var north = rectanglePositions.current[0].latitude;
              }

              return Cesium.Rectangle.fromRadians(west, south, east, north);
            }, false),
            material: new Cesium.Color(72 / 255, 123 / 255, 242 / 255, 0.2),
          },
          polyline: {
            positions: new Cesium.CallbackProperty(() => {
              if (rectanglePositions.current[0].longitude < rectanglePositions.current[1].longitude) {
                var west = rectanglePositions.current[0].longitude;
                var east = rectanglePositions.current[1].longitude;
              } else {
                var west = rectanglePositions.current[1].longitude;
                var east = rectanglePositions.current[0].longitude;
              }

              if (rectanglePositions.current[0].latitude < rectanglePositions.current[1].latitude) {
                var south = rectanglePositions.current[0].latitude;
                var north = rectanglePositions.current[1].latitude;
              } else {
                var south = rectanglePositions.current[1].latitude;
                var north = rectanglePositions.current[0].latitude;
              }

              var coords = [west, south, east, south, east, north, west, north, west, south];

              return new Cesium.Cartesian3.fromRadiansArray(coords);
            }, false),
            width: 1.5,
            material: new Cesium.Color.fromCssColorString('#487BF2'),
            clampToGround: true
          }
        });
      }
    }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);

    mouseHandler.current.setInputAction(function (click) {
      if (rectanglePositions.current.length === 0) {
        rectanglePositions.current.push(getGlobePositionWithTerrain(window.MAIN_CESIUM_VIEWER, click.position));
      } else {
        if (!rectangleAdded.current) {
          rectangleAdded.current = true;
          if (rectanglePositions.current[0].longitude < rectanglePositions.current[1].longitude) {
            var west = rectanglePositions.current[0].longitude;
            var east = rectanglePositions.current[1].longitude;
          } else {
            var west = rectanglePositions.current[1].longitude;
            var east = rectanglePositions.current[0].longitude;
          }

          if (rectanglePositions.current[0].latitude < rectanglePositions.current[1].latitude) {
            var south = rectanglePositions.current[0].latitude;
            var north = rectanglePositions.current[1].latitude;
          } else {
            var south = rectanglePositions.current[1].latitude;
            var north = rectanglePositions.current[0].latitude;
          }

          var polylineCoords = [west, south, east, south, east, north, west, north, west, south];

          window.MAIN_CESIUM_VIEWER.entities.remove(window.exportRectangle);
          window.exportRectangle = window.MAIN_CESIUM_VIEWER.entities.add({
            rectangle: {
              coordinates: Cesium.Rectangle.fromRadians(west, south, east, north),
              material: new Cesium.Color(72 / 255, 123 / 255, 242 / 255, 0.2),
            },
            polyline: {
              positions: Cesium.Cartesian3.fromRadiansArray(polylineCoords),
              width: 1.5,
              material: new Cesium.Color.fromCssColorString('#487BF2'),
              clampToGround: true
            }
          });

          var coords = window.exportRectangle.rectangle.coordinates._value;
          var west = coords.west * Cesium.Math.DEGREES_PER_RADIAN;
          var south = coords.south * Cesium.Math.DEGREES_PER_RADIAN;
          var east = coords.east * Cesium.Math.DEGREES_PER_RADIAN;
          var north = coords.north * Cesium.Math.DEGREES_PER_RADIAN;
          boundaryRef.current = [[west, north], [east, north], [east, south], [west, south], [west, north]]
          setExportBtnActive(true);
        } else {
          window.MAIN_CESIUM_VIEWER.entities.remove(window.exportRectangle);
          delete (window.exportRectangle);
          rectanglePositions.current = [];
          rectangleAdded.current = false;
          rectanglePositions.current.push(getGlobePositionWithTerrain(window.MAIN_CESIUM_VIEWER, click.position));
        }
      }

    }, Cesium.ScreenSpaceEventType.LEFT_CLICK);
    handleCloseMenu();
  }

  const activeShapePoints = useRef([]);
  const circles = useRef([]);
  const floatingPoint = useRef();
  const drawPolygon = () => {
    removeExportRectangle();
    removeExportPolygon();
    window.MAIN_CESIUM_VIEWER.entities.remove(window.kmlExportPolygon);
    delete (window.kmlExportPolygon)
    window.MAIN_CESIUM_VIEWER.entities.remove(window.geojsonExportPolygon);
    delete (window.geojsonExportPolygon)

    document.getElementById("cesium-wrapper").style.cursor = 'crosshair';
    mouseHandler.current = new Cesium.ScreenSpaceEventHandler(window.MAIN_CESIUM_VIEWER.scene.canvas);
    mouseHandler.current.setInputAction(function (click) {
      if (activeShapePoints.current.length === 0) {
        activeShapePoints.current.push(getGlobePositionWithTerrain(window.MAIN_CESIUM_VIEWER, click.position));
        window.exportPolygon = window.MAIN_CESIUM_VIEWER.entities.add({
          polygon: {
            hierarchy: new Cesium.CallbackProperty(function () {
              return new Cesium.PolygonHierarchy(
                Cesium.Ellipsoid.WGS84.cartographicArrayToCartesianArray(activeShapePoints.current)
              );
            }, false),
            material: new Cesium.Color(72 / 255, 123 / 255, 242 / 255, 0.2),
          },
          polyline: {
            positions: new Cesium.CallbackProperty(function () {
              return Cesium.Ellipsoid.WGS84.cartographicArrayToCartesianArray(
                [...activeShapePoints.current, activeShapePoints.current[0]]
              )
            }, false),
            width: 1.5,
            material: new Cesium.Color.fromCssColorString('#487BF2'),
            clampToGround: true
          }
        });

        let circleImage = generateNodeImage('#487BF2', true);
        floatingPoint.current = window.MAIN_CESIUM_VIEWER.entities.add({
          position: Cesium.Cartographic.toCartesian(getGlobePositionWithTerrain(window.MAIN_CESIUM_VIEWER, click.position)),
          billboard: {
            image: circleImage,
            horizontalOrigin: Cesium.HorizontalOrigin.CENTER,
            verticalOrigin: Cesium.VerticalOrigin.CENTER,
            width: 15,
            height: 15,
            heightReference: Cesium.HeightReference.NONE
          }
        });
      }
      if (activeShapePoints.current.length >= 2) {
        var geodesic = new Cesium.EllipsoidGeodesic();
        geodesic.setEndPoints(activeShapePoints.current[0], getGlobePositionWithTerrain(window.MAIN_CESIUM_VIEWER, click.position));
        var lengthInMeters = geodesic.surfaceDistance;
        if (lengthInMeters <= 0.25) {
          activeShapePoints.current.pop();
          ToolTipDisplay(2);
          mouseHandler.current.removeInputAction(Cesium.ScreenSpaceEventType.LEFT_CLICK)
          mouseHandler.current.removeInputAction(Cesium.ScreenSpaceEventType.MOUSE_MOVE)
          document.getElementById("cesium-wrapper").style.cursor = 'auto';

          var boundary = [];
          activeShapePoints.current.map(p => {
            boundary.push([
              p.longitude * Cesium.Math.DEGREES_PER_RADIAN,
              p.latitude * Cesium.Math.DEGREES_PER_RADIAN
            ])
          })
          boundary.push([
            activeShapePoints.current[0].longitude * Cesium.Math.DEGREES_PER_RADIAN,
            activeShapePoints.current[0].latitude * Cesium.Math.DEGREES_PER_RADIAN
          ])
          boundaryRef.current = boundary;
          setExportBtnActive(true);
          return;
        }
      }
      activeShapePoints.current.push(getGlobePositionWithTerrain(window.MAIN_CESIUM_VIEWER, click.position));

      let circleImage = generateNodeImage('#487BF2', true);
      var point = window.MAIN_CESIUM_VIEWER.entities.add({
        position: Cesium.Cartographic.toCartesian(getGlobePositionWithTerrain(window.MAIN_CESIUM_VIEWER, click.position)),
        billboard: {
          image: circleImage,
          horizontalOrigin: Cesium.HorizontalOrigin.CENTER,
          verticalOrigin: Cesium.VerticalOrigin.CENTER,
          width: 15,
          height: 15,
          heightReference: Cesium.HeightReference.NONE
        }
      });
      circles.current.push(point);
    }, Cesium.ScreenSpaceEventType.LEFT_CLICK);

    mouseHandler.current.setInputAction(function (movement) {
      let newPosition = null;
      if (window.exportPolygon) {
        ToolTipDisplay(2);
        newPosition = getGlobePositionWithTerrain(window.MAIN_CESIUM_VIEWER, movement.endPosition);
        if (Cesium.defined(newPosition)) {
          floatingPoint.current.position.setValue(
            Cesium.Cartographic.toCartesian(newPosition)
          );
          activeShapePoints.current.pop();
          activeShapePoints.current.push(newPosition);

          var geodesic = new Cesium.EllipsoidGeodesic();
          geodesic.setEndPoints(activeShapePoints.current[0], newPosition);
          var lengthInMeters = geodesic.surfaceDistance;
          if (lengthInMeters <= 0.25) {
            let pixel_position = Cesium.SceneTransforms.wgs84ToWindowCoordinates(window.MAIN_CESIUM_VIEWER.scene, Cesium.Cartographic.toCartesian(newPosition));
            ToolTipDisplay(1, pixel_position, 'Click to close Polygon');
          }
        }
      } else {
        newPosition = getGlobePositionWithTerrain(window.MAIN_CESIUM_VIEWER, movement.endPosition);
        let pixel_position = Cesium.SceneTransforms.wgs84ToWindowCoordinates(window.MAIN_CESIUM_VIEWER.scene, Cesium.Cartographic.toCartesian(newPosition));
        ToolTipDisplay(1, pixel_position, 'Click for starting point');
      }
    }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);
    handleCloseMenu();
  }

  useEffect(() => {
    setmapboxvisibility(1);
    if (mouseHandler.current) {
      mouseHandler.current.removeInputAction(Cesium.ScreenSpaceEventType.MOUSE_MOVE);
      mouseHandler.current.removeInputAction(Cesium.ScreenSpaceEventType.LEFT_CLICK);
    }
  }, [])

  return (
    <Wrapper>
      <TooltipIconButton title="Close" ariaLabel="close" placement="bottom" onClick={closeModal} size={44} position="true" top={10} right={10} zIndex={10}>
        <Close />
      </TooltipIconButton>
      <Content>
        <TooltipIconButton title="More" ariaLabel="draw-area" placement="top" size={60} position="true" top={-29} right={30} bluebg="true" onClick={handleClickMore} >
          <MoreVert />
        </TooltipIconButton>
        <Menu
          id="basic-menu"
          anchorEl={anchorEl}
          open={menuOpen}
          onClose={handleCloseMenu}
          MenuListProps={{'aria-labelledby': 'basic-button'}}>
          <MenuItem onClick={drawRectangle}>Draw Rectangle</MenuItem>
          <MenuItem onClick={drawPolygon}>Draw Polygon</MenuItem>
        </Menu>
        <h3>Export Print</h3>
        <FormItem>
          <p>Collection</p>
          <FormControl variant="filled" className={classes.formControl}>
            <Select value={formData.catalog} onChange={(e) => handleChange(e, 'catalog')}>
              {window.exportable_collections.map((item, index) => (
                <MenuItem value={item.name} key={index}>{item.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </FormItem>
        <FormItem>
          <p>Maximum Size</p>
          <FormControl variant="filled" className={classes.formControl}>
            <Select value={formData.maxSize} onChange={(e) => handleChange(e, 'maxSize')}>
              {maxSizes.map((item, index) => (
                <MenuItem value={item.value} key={index}>{item.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </FormItem>
        <FormItem>
          <p>Output Format</p>
          <FormControl variant="filled" className={classes.formControl}>
            <Select value={formData.format} onChange={(e) => handleChange(e, 'format')}>
              {formats.map((item, index) => (
                <MenuItem value={item.value} key={index}>{item.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </FormItem>
      </Content>
      {exportBtnActive ?
        <div style={{'border-top': '1px solid rgba(255, 255, 255)'}}>
          <button onClick={printImage} style={{ width: "100%", padding: 0, background: '#2F2F2F' }}>
            <Footer>
              <Wallpaper style={{ color: "white", width: 20, height: 20 }} />
              <span>Print Cropped Area</span>
            </Footer>
          </button>
        </div> 
        :
        <div style={{'border-top': '1px solid rgba(255, 255, 255, 0.75)'}}>
            <button style={{ width: "100%", padding: 0, background: '#2F2F2F' }}>
            <Footer>
              <Wallpaper style={{ color: "grey", width: 20, height: 20 }} />
              <span style={{ color: "grey" }}>Print Cropped Area</span>
            </Footer>
          </button>
        </div>
      }
    </Wrapper>
  );
};

export default DataPrintModal;